import React from 'react'
import { Form } from 'react-bootstrap'
import { Pagination as BootstrapPagination } from 'react-bootstrap'

interface PaginationProps {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
  showFirstLast?: boolean
  maxVisiblePages?: number
  alwaysVisible?: boolean
  pageSize?: number
  pageSizeOptions?: number[]
  onPageSizeChange?: (pageSize: number) => void
}

export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  showFirstLast = true,
  maxVisiblePages = 5,
  alwaysVisible = false,
  pageSize,
  pageSizeOptions = [20, 50, 100],
  onPageSizeChange,
}) => {
  const showPageSizeSelector = onPageSizeChange && pageSize != null && pageSizeOptions.length > 0

  if (!alwaysVisible && totalPages <= 1) {
    return null
  }

  const getVisiblePages = () => {
    const pages: number[] = []
    const halfVisible = Math.floor(maxVisiblePages / 2)

    let startPage = Math.max(1, currentPage - halfVisible)
    let endPage = Math.min(totalPages, currentPage + halfVisible)

    // Adjust if we're near the beginning or end
    if (endPage - startPage + 1 < maxVisiblePages) {
      if (startPage === 1) {
        endPage = Math.min(totalPages, startPage + maxVisiblePages - 1)
      } else {
        startPage = Math.max(1, endPage - maxVisiblePages + 1)
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i)
    }

    return pages
  }

  const visiblePages = getVisiblePages()

  return (
    <div className="d-flex align-items-center gap-2">
      {showPageSizeSelector && (
        <Form.Select
          size="sm"
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
          style={{ width: '88px' }}
        >
          {pageSizeOptions.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </Form.Select>
      )}
      <BootstrapPagination size="sm" className="justify-content-center mb-0">
        {showFirstLast && currentPage > 1 && (
          <>
            <BootstrapPagination.First onClick={() => onPageChange(1)} />
            <BootstrapPagination.Prev onClick={() => onPageChange(currentPage - 1)} />
          </>
        )}

        {visiblePages[0] > 1 && (
          <>
            <BootstrapPagination.Item onClick={() => onPageChange(1)}>1</BootstrapPagination.Item>
            {visiblePages[0] > 2 && <BootstrapPagination.Ellipsis />}
          </>
        )}

        {visiblePages.map((page) => (
          <BootstrapPagination.Item key={page} active={page === currentPage} onClick={() => onPageChange(page)}>
            {page}
          </BootstrapPagination.Item>
        ))}

        {visiblePages[visiblePages.length - 1] < totalPages && (
          <>
            {visiblePages[visiblePages.length - 1] < totalPages - 1 && <BootstrapPagination.Ellipsis />}
            <BootstrapPagination.Item onClick={() => onPageChange(totalPages)}>{totalPages}</BootstrapPagination.Item>
          </>
        )}

        {showFirstLast && currentPage < totalPages && (
          <>
            <BootstrapPagination.Next onClick={() => onPageChange(currentPage + 1)} />
            <BootstrapPagination.Last onClick={() => onPageChange(totalPages)} />
          </>
        )}
      </BootstrapPagination>
    </div>
  )
}

export default Pagination
